import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import {useSettings} from "../contexts/SettingsContext";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];

function toRuSpecies(name) {
    if (!name) return "Неизвестно";
    if (String(name).trim().toLowerCase() === "unknown") return "Неизвестно";
    return name;
}

export default function FishDetectionPage() {
    const { isAuthenticated, ready } = useAuth();
    const { backendSettings, getBackendSetting } = useSettings();
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState([]);
    const [message, setMessage] = useState("");
    const [previewUrl, setPreviewUrl] = useState("");
    const imageRef = useRef(null);
    const canvasRef = useRef(null);
    const wrapperRef = useRef(null);
    const [isAuthRequiredBySettings, setIsAuthRequiredBySettings] = useState(true);
    const [hoverInfo, setHoverInfo] = useState(null);
    const palette = useMemo(() => ["#0ea5e9", "#f472b6", "#34d399", "#fbbf24", "#a78bfa", "#fb923c"], []);

    useEffect(() => {
        setIsAuthRequiredBySettings(getBackendSetting("fish_recognition_auth_required") === '1');
    }, [backendSettings, getBackendSetting]);

    const openLoginModal = () => {
        window.dispatchEvent(new CustomEvent("auth:open-login-modal", {
            detail: {
                nextPath: "/tools/fish-detection",
                title: "Войдите, чтобы распознать рыбу",
            },
        }));
    };

    const validateFile = (nextFile) => {
        if (!nextFile) {
            return "";
        }
        if (!ALLOWED_MIME_TYPES.includes(nextFile.type)) {
            return "Файл должен быть в формате JPG, PNG или WebP.";
        }
        if (nextFile.size > MAX_FILE_BYTES) {
            return "Файл больше 10 МБ.";
        }
        return "";
    };

    const handleFileChange = (event) => {
        const nextFile = event.target.files?.[0] || null;
        const validationError = validateFile(nextFile);

        setHoverInfo(null);
        setResult([]);
        setFile(validationError ? null : nextFile);
        setMessage(validationError);
        event.target.value = "";
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        if (!isAuthenticated && isAuthRequiredBySettings) {
            openLoginModal();
            return;
        }
        if (!file) {
            setMessage("Выберите фото.");
            return;
        }
        const validationError = validateFile(file);
        if (validationError) {
            setMessage(validationError);
            return;
        }
        setLoading(true);
        setResult([]);
        setMessage("");
        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await axios.post(`${API_BASE_URL}/api/ml/recognize-fish`, formData, { withCredentials: true });
            const rows = Array.isArray(response.data?.results)
                ? response.data.results
                : Array.isArray(response.data)
                    ? response.data
                    : [];
            setResult(rows);
        } catch (error) {
            const status = error?.response?.status;
            if (status === 401 || status === 403) {
                openLoginModal();
                setMessage("");
                return;
            }
            setMessage(error?.response?.data?.error || "Не удалось распознать рыбу.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!file) {
            setPreviewUrl("");
            return undefined;
        }

        const next = URL.createObjectURL(file);
        setPreviewUrl(next);

        return () => URL.revokeObjectURL(next);
    }, [file]);

    const getModelToImageScale = (width, height) => {
        if (!width || !height) return 1;
        const maxDim = Math.max(width, height);
        if (maxDim <= 1024) return 1;
        // Backend scales image down to max 1024px before inference.
        return maxDim / 1024;
    };

    useEffect(() => {
        if (!previewUrl || !imageRef.current || !canvasRef.current) {
            return;
        }

        const image = imageRef.current;
        const canvas = canvasRef.current;

        const drawRoundRect = (ctx, x, y, w, h, r) => {
            const radius = Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2);
            ctx.beginPath();
            ctx.moveTo(x + radius, y);
            ctx.lineTo(x + w - radius, y);
            ctx.arcTo(x + w, y, x + w, y + radius, radius);
            ctx.lineTo(x + w, y + h - radius);
            ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
            ctx.lineTo(x + radius, y + h);
            ctx.arcTo(x, y + h, x, y + h - radius, radius);
            ctx.lineTo(x, y + radius);
            ctx.arcTo(x, y, x + radius, y, radius);
            ctx.closePath();
        };

        const draw = () => {
            const width = image.naturalWidth || image.width;
            const height = image.naturalHeight || image.height;
            if (!width || !height) return;
            const modelToImageScale = getModelToImageScale(width, height);

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            if (!ctx) return;
            ctx.clearRect(0, 0, width, height);

            result.forEach((item, index) => {
                const color = palette[index % palette.length];
                const mask = Array.isArray(item.mask) ? item.mask : [];
                const bbox = Array.isArray(item.bounding_box) ? item.bounding_box : [];
                const species = Array.isArray(item.species) ? item.species : [];
                const accuracy = Array.isArray(item.accuracy) ? item.accuracy : [];

                if (mask.length > 1) {
                    ctx.beginPath();
                    ctx.moveTo(mask[0][0] * modelToImageScale, mask[0][1] * modelToImageScale);
                    for (let i = 1; i < mask.length; i += 1) {
                        ctx.lineTo(mask[i][0] * modelToImageScale, mask[i][1] * modelToImageScale);
                    }
                    ctx.closePath();
                    ctx.fillStyle = `${color}30`;
                    ctx.fill();
                    ctx.lineJoin = "round";
                    ctx.lineWidth = 1.5;
                    ctx.strokeStyle = `${color}bb`;
                    ctx.stroke();
                }

                if (bbox.length === 4) {
                    const [rawX1, rawY1, rawX2, rawY2] = bbox;
                    const x1 = rawX1 * modelToImageScale;
                    const y1 = rawY1 * modelToImageScale;
                    const x2 = rawX2 * modelToImageScale;
                    const y2 = rawY2 * modelToImageScale;
                    const bw = Math.max(0, x2 - x1);
                    const bh = Math.max(0, y2 - y1);

                    drawRoundRect(ctx, x1, y1, bw, bh, 10);
                    ctx.lineWidth = 1.5;
                    ctx.strokeStyle = `${color}cc`;
                    ctx.setLineDash([]);
                    ctx.stroke();

                    const mainName = toRuSpecies(species[0] || "Неизвестно");
                    const mainAcc = typeof accuracy[0] === "number" ? `${Math.round(accuracy[0] * 100)}%` : "";
                    const label = `${mainName}${mainAcc ? `  ${mainAcc}` : ""}`;
                    const fontSize = Math.max(12, Math.min(16, width / 40));
                    ctx.font = `600 ${fontSize}px Roboto, "Open Sans", Arial, sans-serif`;
                    const textWidth = ctx.measureText(label).width;
                    const dotR = fontSize * 0.32;
                    const padH = fontSize * 0.55;
                    const padV = fontSize * 0.38;
                    const pillW = textWidth + dotR * 2 + padH * 2 + 6;
                    const pillH = fontSize + padV * 2;
                    const pillX = x1;
                    const pillY = Math.max(0, y1 - pillH - 4);

                    drawRoundRect(ctx, pillX, pillY, pillW, pillH, pillH / 2);
                    ctx.fillStyle = "rgba(15, 23, 42, 0.72)";
                    ctx.fill();

                    ctx.beginPath();
                    ctx.arc(pillX + padH + dotR, pillY + pillH / 2, dotR, 0, Math.PI * 2);
                    ctx.fillStyle = color;
                    ctx.fill();

                    ctx.fillStyle = "#ffffff";
                    ctx.textBaseline = "middle";
                    ctx.fillText(label, pillX + padH + dotR * 2 + 6, pillY + pillH / 2);
                    ctx.textBaseline = "alphabetic";
                }
            });
        };

        if (image.complete) {
            draw();
        } else {
            image.onload = draw;
        }
    }, [previewUrl, result, palette]);

    const getMainLabel = (item, index) => {
        const species = Array.isArray(item?.species) ? item.species : [];
        const accuracy = Array.isArray(item?.accuracy) ? item.accuracy : [];
        const mainName = toRuSpecies(species[0] || "Неизвестно");
        const mainAccuracy = typeof accuracy[0] === "number" ? `${Math.round(accuracy[0] * 100)}%` : "0%";
        return `Объект ${index + 1} - ${mainName} ${mainAccuracy}`;
    };

    const pointInPolygon = (x, y, polygon) => {
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const xi = polygon[i][0], yi = polygon[i][1];
            const xj = polygon[j][0], yj = polygon[j][1];
            const intersect = ((yi > y) !== (yj > y)) &&
                (x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-9) + xi);
            if (intersect) inside = !inside;
        }
        return inside;
    };

    const handleCanvasMove = (event) => {
        if (!canvasRef.current || !wrapperRef.current) return;
        const image = imageRef.current;
        if (!image) return;
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = (event.clientX - rect.left) * scaleX;
        const y = (event.clientY - rect.top) * scaleY;
        const modelToImageScale = getModelToImageScale(image.naturalWidth || image.width, image.naturalHeight || image.height);
        const imageToModelScale = modelToImageScale > 0 ? 1 / modelToImageScale : 1;
        const modelX = x * imageToModelScale;
        const modelY = y * imageToModelScale;

        let hitIndex = -1;
        for (let i = 0; i < result.length; i += 1) {
            const item = result[i];
            const mask = Array.isArray(item?.mask) ? item.mask : [];
            const bbox = Array.isArray(item?.bounding_box) ? item.bounding_box : [];
            if (mask.length > 2 && pointInPolygon(modelX, modelY, mask)) {
                hitIndex = i;
                break;
            }
            if (bbox.length === 4) {
                const [x1, y1, x2, y2] = bbox;
                if (modelX >= x1 && modelX <= x2 && modelY >= y1 && modelY <= y2) {
                    hitIndex = i;
                    break;
                }
            }
        }

        if (hitIndex >= 0) {
            const wrapperRect = wrapperRef.current.getBoundingClientRect();
            setHoverInfo({
                text: getMainLabel(result[hitIndex], hitIndex),
                x: event.clientX - wrapperRect.left + 10,
                y: event.clientY - wrapperRect.top + 10,
            });
            canvas.style.cursor = "pointer";
        } else {
            setHoverInfo(null);
            canvas.style.cursor = "default";
        }
    };

    const handleCanvasLeave = () => {
        setHoverInfo(null);
        if (canvasRef.current) {
            canvasRef.current.style.cursor = "default";
        }
    };

    const ranked = useMemo(() => {
        return result.map((item, idx) => {
            const species = Array.isArray(item.species) ? item.species : [];
            const accuracy = Array.isArray(item.accuracy) ? item.accuracy : [];

            return {
                id: idx + 1,
                candidates: species.map((name, i) => ({
                    name: toRuSpecies(name),
                    accuracy: typeof accuracy[i] === "number" ? Math.round(accuracy[i] * 10000) / 100 : 0,
                })),
            };
        });
    }, [result]);

    return (
        <div className="blog-page-root">
            <div className="blog-page-inner fish-detection-inner">

                <div className="blog-page-header">
                    <div className="blog-head">
                        <h2 className="blog-page-title">Определить рыбу по фото</h2>
                    </div>
                </div>

                <div className="cms-page-panel fish-detection-form-panel">
                    <p className="fish-detection-hint">
                        Загрузите фото улова — нейросеть FishMap подскажет возможный вид рыбы с оценкой уверенности.
                        Удобно, если поймали незнакомый экземпляр или хотите проверить догадку по внешнему виду.
                    </p>
                    <p className="fish-detection-hint" style={{ marginTop: 4 }}>JPG, PNG или WebP, до 10 МБ.</p>
                    <form onSubmit={onSubmit} style={{ marginTop: 20 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <label className="btn-outline" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                                <span>{file ? "Другое фото" : "Фото улова"}</span>
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={handleFileChange}
                                    style={{ display: "none" }}
                                />
                            </label>
                            <button
                                className={`btn-outline${!isAuthenticated && ready && isAuthRequiredBySettings ? " btn-outline--inactive" : ""}`}
                                type="submit"
                                disabled={loading || (isAuthenticated && !file)}
                                onClick={!isAuthenticated && ready && isAuthRequiredBySettings ? (event) => {
                                    event.preventDefault();
                                    openLoginModal();
                                } : undefined}
                                aria-disabled={!isAuthenticated && ready && isAuthRequiredBySettings ? "true" : undefined}
                            >
                                {loading ? "Распознаём..." : "Распознать"}
                            </button>
                        </div>
                    </form>
                    {ready && !isAuthenticated && isAuthRequiredBySettings && (
                        <p className="form-message" style={{ marginTop: 16 }}>
                            Для распознавания необходимо авторизоваться.
                        </p>
                    )}
                    {!!message && <p className="form-message" style={{ marginTop: 16 }}>{message}</p>}
                </div>

                {!!previewUrl && (
                    <div className="cms-page-panel fish-detection-preview" style={{ marginTop: 16 }}>
                        <div className="fish-detection-preview__image">
                            <p className="fish-detection-section-label">Изображение и маска</p>
                            <div ref={wrapperRef} style={{ position: "relative", width: "100%", marginTop: 10 }}>
                                <img
                                    ref={imageRef}
                                    src={previewUrl}
                                    alt="Загруженное изображение"
                                    style={{ width: "100%", display: "block", borderRadius: 12 }}
                                />
                                <canvas
                                    ref={canvasRef}
                                    style={{
                                        position: "absolute",
                                        left: 0, top: 0,
                                        width: "100%", height: "100%",
                                        pointerEvents: "auto",
                                    }}
                                    onMouseMove={handleCanvasMove}
                                    onMouseLeave={handleCanvasLeave}
                                />
                                {!!hoverInfo && (
                                    <div style={{
                                        position: "absolute",
                                        left: hoverInfo.x,
                                        top: hoverInfo.y,
                                        background: "rgba(15, 23, 42, 0.88)",
                                        backdropFilter: "blur(8px)",
                                        color: "#fff",
                                        padding: "6px 10px",
                                        borderRadius: 8,
                                        fontSize: 12,
                                        lineHeight: 1.4,
                                        pointerEvents: "none",
                                        maxWidth: 280,
                                    }}>
                                        {hoverInfo.text}
                                    </div>
                                )}
                            </div>
                        </div>
                        <aside className="fish-detection-preview__legend">
                            <p className="fish-detection-section-label">Легенда</p>
                            {!ranked.length && (
                                <p className="fish-detection-hint" style={{ marginTop: 6 }}>
                                    После распознавания здесь появится список объектов.
                                </p>
                            )}
                            {!!ranked.length && (
                                <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                                    {ranked.map((row) => (
                                        <div key={`legend-${row.id}`} className="fish-detection-legend-item">
                                            <strong style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                                <span style={{
                                                    width: 12, height: 12, borderRadius: 999,
                                                    background: palette[(row.id - 1) % palette.length],
                                                    display: "inline-block",
                                                    border: "1px solid rgba(0,0,0,0.15)",
                                                    flexShrink: 0,
                                                }} />
                                                Объект #{row.id}
                                            </strong>
                                            <div style={{ marginTop: 4, fontSize: 13, color: "rgba(15,23,42,0.60)" }}>
                                                {row.candidates[0]?.name || "Не удалось определить вид"}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </aside>
                    </div>
                )}

                {!!ranked.length && (
                    <div className="cms-page-panel" style={{ marginTop: 16 }}>
                        <p className="fish-detection-section-label" style={{ marginBottom: 16 }}>Наиболее подходящие виды</p>
                        {ranked.map((row) => (
                            <div key={row.id} className="fish-detection-result-row">
                                <strong style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(15,23,42,0.85)" }}>
                                    Объект #{row.id}
                                    <span title="Цвет маски объекта" style={{
                                        width: 12, height: 12, borderRadius: 999,
                                        background: palette[(row.id - 1) % palette.length],
                                        display: "inline-block",
                                        border: "1px solid rgba(0,0,0,0.15)",
                                        flexShrink: 0,
                                    }} />
                                </strong>
                                {row.candidates.length === 0 && (
                                    <p style={{ margin: "6px 0 0", color: "rgba(15,23,42,0.50)" }}>Не удалось определить вид.</p>
                                )}
                                {row.candidates.length > 0 && (
                                    <ol style={{ margin: "8px 0 0", paddingLeft: 20, color: "rgba(15,23,42,0.72)", fontSize: 14, lineHeight: 1.8 }}>
                                        {row.candidates.map((candidate) => (
                                            <li key={`${row.id}-${candidate.name}`}>
                                                {candidate.name} — <span style={{ color: "var(--primary)", fontWeight: 600 }}>{candidate.accuracy}%</span>
                                            </li>
                                        ))}
                                    </ol>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
